import { create } from 'zustand';

interface FinanceItem {
  label: string;
  unit: string;
  val: number;
  min?: number;
  max?: number;
}

interface FinanceState {
  exploitation: Record<string, FinanceItem>;
  useForfait: boolean;
  forfait: number;
  revenus: Record<string, FinanceItem>;
  emploisOverride: number | null;
  chgGrowth: number;
  revGrowth: number;
  centimes: number;
  creditMontant: number;
  creditInteret: number;
  surfaceVoirie: number;
  classesExtension: number;
  abrisPlaces: number;
  /** v11: share of non-residents among mixed-zone jobs (%). Default 50. */
  partNRmix: number;
  /** v11: share of non-residents among ZI jobs (%). Default 80. */
  partNRzi: number;

  setExpItem: (key: string, val: number) => void;
  setRevItem: (key: string, val: number) => void;
  setUseForfait: (v: boolean) => void;
  setForfait: (v: number) => void;
  setEmploisOverride: (v: number | null) => void;
  setChgGrowth: (v: number) => void;
  setRevGrowth: (v: number) => void;
  setCentimes: (v: number) => void;
  setCreditMontant: (v: number) => void;
  setCreditInteret: (v: number) => void;
  setSurfaceVoirie: (v: number) => void;
  setClassesExtension: (v: number) => void;
  setAbrisPlaces: (v: number) => void;
  setPartNRmix: (v: number) => void;
  setPartNRzi: (v: number) => void;
  resetFinance: () => void;
  hydrate: (data: Partial<Omit<FinanceState, 'setExpItem' | 'setRevItem' | 'setUseForfait' | 'setForfait' | 'setEmploisOverride' | 'setChgGrowth' | 'setRevGrowth' | 'setCentimes' | 'setCreditMontant' | 'setCreditInteret' | 'setSurfaceVoirie' | 'setClassesExtension' | 'setAbrisPlaces' | 'setPartNRmix' | 'setPartNRzi' | 'resetFinance' | 'hydrate'>>) => void;
}

const DEFAULT_EXPLOITATION: Record<string, FinanceItem> = {
  personnel:    { label: 'Charges de personnel',       unit: 'CHF/hab',   val: 655 },
  securite:     { label: 'Ordre et sécurité publique', unit: 'CHF/hab',   val: 134 },
  formation:    { label: 'Formation',                  unit: 'CHF/hab',   val: 168 },
  sport:        { label: 'Sport (hors entretien)',     unit: 'CHF/hab',   val: 22 },
  culture:      { label: 'Culture',                    unit: 'CHF/hab',   val: 211 },
  social:       { label: 'Aide sociale',               unit: 'CHF/hab',   val: 45 },
  dechets:      { label: 'Environnement / Déchets',    unit: 'CHF/hab',   val: 11 },
  autres:       { label: 'Autres',                     unit: 'CHF/hab',   val: 240 },
  exploitation: { label: "Charges d'exploitation",     unit: 'CHF/hab',   val: 667 },
  creches:      { label: 'Crèches (net communal)',     unit: 'CHF/place', val: 38000 },
};

const DEFAULT_REVENUS: Record<string, FinanceItem> = {
  impots_revenu:  { label: 'Impôt communal sur le revenu',     unit: 'CHF/contrib', val: 1120, min: 1120, max: 1302 },
  impots_fortune: { label: 'Impôt communal sur la fortune',    unit: 'CHF/contrib', val: 79,   min: 79,   max: 93 },
  impots_pm:      { label: 'Impôts entreprises (PM)',           unit: 'CHF/emploi',  val: 800 },
  peréquation:    { label: 'Péréquation intercommunale',        unit: 'CHF/an',      val: 1000000 },
  autres_rev:     { label: 'Revenus immobiliers, fonciers, recettes indirectes, induits et autres', unit: '5% autres', val: 0 },
  impots_source:  { label: 'Impôt à la source',                unit: 'CHF/contrib', val: 204, min: 200, max: 250 },
  taxes:          { label: 'Taxes',                             unit: 'CHF/contrib', val: 144, min: 140, max: 180 },
  impots_nr:      { label: 'Impôts sur travailleurs non-résidents', unit: 'CHF/emploi', val: 2250, min: 2000, max: 2500 },
};

export const useFinanceStore = create<FinanceState>((set) => ({
  exploitation: structuredClone(DEFAULT_EXPLOITATION),
  useForfait: false,
  forfait: 2500,
  revenus: structuredClone(DEFAULT_REVENUS),
  emploisOverride: null,
  chgGrowth: 0,
  revGrowth: 0,
  centimes: 46,
  creditMontant: 0,
  creditInteret: 2.0,
  surfaceVoirie: 0,
  classesExtension: 0,
  abrisPlaces: 0,
  partNRmix: 50,
  partNRzi: 80,

  setExpItem: (key, val) =>
    set((state) => {
      const item = state.exploitation[key];
      if (!item) return state;
      return { exploitation: { ...state.exploitation, [key]: { ...item, val } } };
    }),
  setRevItem: (key, val) =>
    set((state) => {
      const item = state.revenus[key];
      if (!item) return state;
      return { revenus: { ...state.revenus, [key]: { ...item, val } } };
    }),
  setUseForfait: (useForfait) => set({ useForfait }),
  setForfait: (forfait) => set({ forfait }),
  setEmploisOverride: (emploisOverride) => set({ emploisOverride }),
  setChgGrowth: (chgGrowth) => set({ chgGrowth }),
  setRevGrowth: (revGrowth) => set({ revGrowth }),
  setCentimes: (centimes) => set({ centimes }),
  setCreditMontant: (creditMontant) => set({ creditMontant }),
  setCreditInteret: (creditInteret) => set({ creditInteret }),
  setSurfaceVoirie: (surfaceVoirie) => set({ surfaceVoirie }),
  setClassesExtension: (classesExtension) => set({ classesExtension }),
  setAbrisPlaces: (abrisPlaces) => set({ abrisPlaces }),
  setPartNRmix: (partNRmix) => set({ partNRmix: Math.max(0, Math.min(100, partNRmix)) }),
  setPartNRzi: (partNRzi) => set({ partNRzi: Math.max(0, Math.min(100, partNRzi)) }),
  resetFinance: () =>
    set({
      exploitation: structuredClone(DEFAULT_EXPLOITATION),
      useForfait: false,
      forfait: 2500,
      revenus: structuredClone(DEFAULT_REVENUS),
      emploisOverride: null,
      chgGrowth: 0,
      revGrowth: 0,
      centimes: 46,
      creditMontant: 0,
      creditInteret: 2.0,
      surfaceVoirie: 0,
      classesExtension: 0,
      abrisPlaces: 0,
      partNRmix: 50,
      partNRzi: 80,
    }),
  hydrate: (data) => set(data),
}));
